import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import {
  DEFAULT_TAKE,
  MIN_SEARCH_LENGTH,
  USER_PICKER_TAKE,
  type CreateUserRequest,
  type GrantableRole,
  type UpdateUserRequest,
  type UserDetailResponse,
  type UserEntitlementsResponse,
  type SubmitCandidatesResponse,
  type UserListResponse,
  type UserSummaryResponse,
  type UsersListParams,
} from '../types';

/**
 * Query-key registry for the users feature. `all` is the invalidation root —
 * every mutation invalidates it so the roster, any open detail, and the manager
 * picker all re-fetch after a write.
 */
export const usersKeys = {
  all: ['users'] as const,
  list: (params: UsersListParams) => ['users', 'list', params] as const,
  detail: (id: string) => ['users', 'detail', id] as const,
  entitlements: (id: string) => ['users', 'entitlements', id] as const,
  /** One entry per settled search term — the picker caches per term, not per screen. */
  options: (search: string) => ['users', 'options', search] as const,
  /** Same per-term keying as `options` — this read is a type-ahead too (BUG-042). */
  submitCandidates: (search: string) => ['users', 'submit-candidates', search] as const,
};

/**
 * The **filter** half of the roster query — dropping empty/short filters the API rejects, and
 * carrying no paging.
 *
 * Split out for the CSV export (AU-61), which sends the filters and no `take`/`skip`. One
 * builder for both is what keeps the export honest: the file is the set the table is showing,
 * so a filter cannot reach one and not the other, and a one-character search term has to be
 * dropped by both (the API 400s it) rather than widening the file.
 */
function buildListFilters(params: UsersListParams): Record<string, string | number | boolean> {
  const q: Record<string, string | number | boolean> = {};
  if (params.role) q.role = params.role;
  if (params.active) q.active = params.active;
  const search = params.search?.trim() ?? '';
  if (search.length >= MIN_SEARCH_LENGTH) q.search = search;
  if (typeof params.isBuyUpEnabled === 'boolean') q.isBuyUpEnabled = params.isBuyUpEnabled;
  return q;
}

/** Builds the axios query object, dropping empty/short filters the API rejects. */
function buildListQuery(params: UsersListParams): Record<string, string | number | boolean> {
  return {
    take: params.take ?? DEFAULT_TAKE,
    skip: params.skip ?? 0,
    ...buildListFilters(params),
  };
}

/**
 * Path + query for the Users screen's CSV export — `GET /users/export`. Spread straight onto
 * `<ExportButton {...} />`.
 *
 * <p>The file is the **whole** filtered roster, not the page on screen: the list serves 50 rows
 * and refuses more than 200, so no paging parameter is sent and the server applies no ceiling
 * on this route.</p>
 */
export function userExportRequest(params: UsersListParams) {
  return { path: '/users/export', params: buildListFilters(params) };
}

/** `GET /api/v1/users` — paged roster with role/active/search/buy-up filters. */
export function useUsersList(params: UsersListParams) {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<UserListResponse>('/users', {
        params: buildListQuery(params),
        signal,
      });
      return data;
    },
    // Keep the previous page visible while the next filter/page resolves so the
    // table doesn't flash empty between server-driven queries.
    placeholderData: keepPreviousData,
  });
}

/** `GET /api/v1/users/{id}` — full profile; disabled until an id is known. */
export function useUserDetail(id: string | undefined) {
  return useQuery({
    queryKey: usersKeys.detail(id ?? ''),
    enabled: !!id,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<UserDetailResponse>(`/users/${id}`, { signal });
      return data;
    },
  });
}

/**
 * `GET /api/v1/users/{id}/entitlements` — the resolved effective budget +
 * equipment package for the detail hero (CAT read model). Disabled until an id
 * is known; identity-stable within a view, so a modest staleTime.
 */
export function useUserEntitlements(id: string | undefined) {
  return useQuery({
    queryKey: usersKeys.entitlements(id ?? ''),
    enabled: !!id,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<UserEntitlementsResponse>(`/users/${id}/entitlements`, {
        signal,
      });
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * `GET /api/v1/me/submit-candidates` — the users the caller may raise a request
 * for (BUG-031). Reachable by every role, unlike `GET /api/v1/users`, and the
 * server returns exactly the set its own on-behalf authorization would accept:
 * the active roster for a RequestDelegate / Manager / Administrator, and just
 * the caller for anyone else.
 *
 * **Server-searched and capped (BUG-042).** This read used to hand back the whole
 * active roster so the browser could filter it — thousands of rows on every
 * request-form mount for a large customer's Administrator. `search` is the
 * settled term from `useTypeahead`; the server applies the same predicate as
 * every other user picker and returns one page plus `hasMore`.
 */
export function useSubmitCandidates(enabled = true, search = '') {
  const term = search.trim();
  const effectiveTerm = term.length >= MIN_SEARCH_LENGTH ? term : '';
  return useQuery({
    queryKey: usersKeys.submitCandidates(effectiveTerm),
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<SubmitCandidatesResponse>('/me/submit-candidates', {
        params: {
          take: USER_PICKER_TAKE,
          ...(effectiveTerm ? { search: effectiveTerm } : {}),
        },
        signal,
      });
      // `appliedTerm` is the term this page answers. `keepPreviousData` below
      // deliberately serves the previous term's page while the next resolves, so
      // the consumer needs a way to tell "these rows are for what you typed" from
      // "these rows are the last answer" — see `typeaheadPage` (BUG-043).
      return { ...data, appliedTerm: effectiveTerm };
    },
    // Hold the previous term's page while the next resolves, so the dropdown
    // doesn't flash empty between keystrokes.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/**
 * The active-user page behind every customer-scoped user picker — the manager
 * picker, the named-approver picker, the budget-override picker and the
 * Administrator "View as user" launcher.
 *
 * `search` is the **settled** term (debounce it with `useDebouncedValue`) and is
 * sent to the server, which filters the whole roster by email / first name /
 * last name / employee number. That is the point: before BUG-034 these pickers
 * fetched one 200-row page and filtered it in the browser, so user 201 could
 * never be selected. Terms shorter than {@link MIN_SEARCH_LENGTH} are dropped by
 * `buildListQuery` (the API rejects them), which harmlessly re-serves the
 * unsearched first page.
 *
 * `keepPreviousData` holds the previous page on screen while the next term
 * resolves, so the dropdown doesn't flash empty between keystrokes. `enabled`
 * gates the fetch for always-mounted-but-closed dialogs — pass their `open`.
 */
export function useUserOptions(search = '', enabled = true) {
  const term = search.trim();
  const effectiveTerm = term.length >= MIN_SEARCH_LENGTH ? term : '';
  return useQuery({
    queryKey: usersKeys.options(effectiveTerm),
    enabled,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<UserListResponse>('/users', {
        params: buildListQuery({ active: 'true', take: USER_PICKER_TAKE, search: effectiveTerm }),
        signal,
      });
      // See `useSubmitCandidates` — `appliedTerm` is what lets a picker tell an
      // answer to the current term from a held-over previous one (BUG-043).
      return { ...data, appliedTerm: effectiveTerm };
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** `POST /api/v1/users` — create. Returns the created detail. */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateUserRequest) => {
      const { data } = await apiClient.post<UserDetailResponse>('/users', body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

/** `PUT /api/v1/users/{id}` — edit. Refreshes the cached detail on success. */
export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateUserRequest) => {
      const { data } = await apiClient.put<UserDetailResponse>(`/users/${id}`, body);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(usersKeys.detail(id), data);
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

/** `POST /api/v1/users/{id}/deactivate` — soft-deactivate (flips isActive). No
 *  reactivate affordance exists in the customer UI yet — see TD-204. */
export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<UserDetailResponse>(`/users/${id}/deactivate`);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(usersKeys.detail(data.id), data);
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

/** A single grant/revoke intent produced by diffing the manage-roles form. */
export interface RoleChange {
  role: GrantableRole;
  grant: boolean;
}

export interface ManageRolesInput {
  id: string;
  changes: RoleChange[];
}

/**
 * Applies the manage-roles diff as per-role grant/revoke calls
 * (`POST|DELETE /api/v1/users/{id}/roles/{role}`). Sequential so a mid-list
 * failure (e.g. inactive target) surfaces without firing later calls; each
 * endpoint is idempotent.
 *
 * The sequence is **not** transactional: if an earlier grant succeeds and a
 * later revoke fails, the server holds a partially-applied (possibly more
 * privileged) set. Invalidation therefore runs in `onSettled` — on error as
 * well as success — so the roster and detail always re-fetch the server's
 * actual roles rather than leaving the UI asserting the pre-change state.
 */
export function useManageRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, changes }: ManageRolesInput) => {
      for (const change of changes) {
        if (change.grant) {
          await apiClient.post(`/users/${id}/roles/${change.role}`);
        } else {
          await apiClient.delete(`/users/${id}/roles/${change.role}`);
        }
      }
    },
    onSettled: (_data, _error, { id }) => {
      void qc.invalidateQueries({ queryKey: usersKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export type { UserSummaryResponse };
